import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    RefreshCw,
    Download,
    AlertTriangle,
    ExternalLink,
    ShieldCheck,
    FileText,
    Info,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek, fmtPct } from '../constants';
import FilingSubmissionPanel from '../components/FilingSubmissionPanel';
import NeRevenueChoice from '../components/NeRevenueChoice';
import SkvStyles, { SkvSeg } from '../components/SkvStyles';

// Skarpa filöverföringstjänsten (kräver e-legitimation). Verifierad mot
// skatteverket.se/filoverforing 2026-08-13 — länken "Inloggning" där.
// OBS: www1.skatteverket.se/fv/fv_web är TESTTJÄNSTEN ("Testa filer") —
// gamla länken hit öppnade dessutom en felsida (systemval.do utan session).
const FILOVERFORING_URL = 'https://sso.skatteverket.se/fv_ext/fv_web/login.do';

/**
 * Frågetecken-popover (samma mönster som skatteplaneringssidans InfoIcon):
 * lagbaserad förklaring vid hovring — aldrig ett tyst antagande i fältet.
 */
const InfoHover = ({ id, label, children }) => (
    <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        overlay={(
            <Popover id={id} style={{ maxWidth: 360 }}>
                <Popover.Header as="h3" style={{ fontSize: '0.8125rem' }}>{label}</Popover.Header>
                <Popover.Body style={{ fontSize: '0.8125rem', lineHeight: 1.55 }}>{children}</Popover.Body>
            </Popover>
        )}
    >
        <button type="button" className="ne-info-btn" aria-label={`Hjälp: ${label}`}>
            <Info size={14} />
        </button>
    </OverlayTrigger>
);

/** Rows we show on sida 2, in blankett order. Editable rader döljs vid noll. */
const R_ROWS = [
    ['R12', 'Bokfört resultat', false],
    ['R13', 'Bokförda kostnader som inte ska dras av', true],
    ['R14', 'Bokförda intäkter som inte ska tas upp', true],
    ['R15', 'Intäkter som inte bokförts men ska tas upp', true],
    ['R16', 'Kostnader som inte bokförts men ska dras av', true],
    ['R17', 'Sammanlagt resultat av verksamheten', false],
    ['R18', 'Gemensam verksamhet — överskott redovisas av annan', true],
    ['R19', 'Gemensam verksamhet — underskott redovisas av annan', true],
    ['R20', 'Medhjälpande make (netto)', true],
    ['R21', 'Min andel av resultatet', true],
    ['R22', 'Övriga skattemässiga justeringar, kostnader', true],
    ['R23', 'Övriga skattemässiga justeringar, intäkter', true],
    ['R24', 'Outnyttjat underskott från föregående år', true],
    ['R25', 'Skogsavdrag/substansminskningsavdrag (N8)', true],
    ['R26', 'Återföring av värdeminskningsavdrag m.m. vid avyttring', true],
    ['R27', 'Uttag från skogs-/upphovsmannakonto', true],
    ['R28', 'Inbetalning till skogs-/upphovsmannakonto', true],
    ['R29', 'Överskott före räntefördelning', false],
    ['R30', 'Positiv räntefördelning', true],
    ['R31', 'Negativ räntefördelning', true],
    ['R32', 'Återföring av periodiseringsfond', true],
    ['R33', 'Överskott före avsättning till periodiseringsfond', false],
    ['R34', 'Avsättning till periodiseringsfond', true],
    ['R35', 'Överskott före ökning av expansionsfond', false],
    ['R36', 'Ökning av expansionsfond', true],
    ['R37', 'Minskning av expansionsfond', true],
    ['R38', 'Egna pensionspremier i näringsverksamheten', true],
    ['R39', 'Särskild löneskatt på pensionssparavdraget', true],
    ['R40', 'Föregående års medgivna schablonavdrag', true],
    ['R41', 'Påförda egenavgifter enligt slutskattebesked', true],
    ['R42', 'Överskott före avdrag för egenavgifter', false],
    ['R43', 'Årets beräknade avdrag för egenavgifter/särskild löneskatt', false],
    ['R44', 'Sjukpenning hänförlig till näringsverksamheten', true],
    ['R45', 'Allmänt avdrag — utnyttjat underskott', true],
    ['R46', 'Underskott utnyttjat i kapital', true],
    ['R47', 'Överskott — förs till INK1', false],
    ['R48', 'Underskott — sparas till nästa år', false],
];

/** U1–U4: obligatoriska upplysningar i K1:s uppställningsform (BFNAR 2006:1 p. 8.1). */
const U_FIELDS = [
    ['u1Periodiseringsfonder', 'U1. Periodiseringsfonder'],
    ['u2Expansionsfond', 'U2. Expansionsfond'],
    ['u3Ersattningsfond', 'U3. Ersättningsfond'],
    ['u4Ovrigt', 'U4. Insatsemissioner, skogskonto m.m.'],
];

const KOHORTER = [
    ['aktiv_full', 'Aktiv näringsverksamhet, full egenavgift (25 %)'],
    ['aktiv_reducerad', 'Endast ålderspensionsavgift (10 %)'],
    ['passiv', 'Passiv näringsverksamhet — särskild löneskatt (20 %)'],
    ['dodsbo', 'Dödsbo — särskild löneskatt (20 %)'],
    ['ingen_avgift', 'Inga egenavgifter (född 1937 eller tidigare)'],
];

/**
 * Deklarationsbeslut, grupperade fråge­drivet (ÅO-mönstret "vanligaste /
 * visa alla"). Motorn (NeComputationCore) har tagit emot SAMTLIGA poster
 * sedan P5 — bara inmatningen saknades, så en firma med t.ex. ej
 * avdragsgilla kostnader (R13) kunde inte deklarera rätt (D16/D18, Codex
 * R6 — release-blockern som stängs här). Grupp[0] visas alltid; övriga
 * bakom "Visa fler poster".
 * Form: [gruppnamn, grupphjälp, [[key, label, help], …]]
 */
const DECISION_GROUPS = [
    ['Vanligaste posterna', null, [
        ['positivRantefordelning', 'Positiv räntefördelning (R30)', 'Flyttar en del av överskottet till kapitalbeskattning (30 %). Högst överskottet vid R29 — och högst årets fördelningsbelopp plus sparat fördelningsbelopp.'],
        ['sparatFordelningsbelopp', 'Sparat fördelningsbelopp från tidigare år (kr)', 'Outnyttjat positivt fördelningsbelopp från tidigare år (IL 33 kap. 7 §) — får användas utöver årets belopp, från 2025 även vid negativt kapitalunderlag. Se hjälpblankett SKV 2196.'],
        ['negativRantefordelning', 'Negativ räntefördelning (R31)', 'Obligatorisk när kapitalunderlaget är lägre än −500 000 kr.'],
        ['aterforingPfond', 'Återföring av periodiseringsfond (R32)', 'Fonder ska återföras senast sjätte året efter avsättningen.'],
        ['avsattningPfond', 'Avsättning till periodiseringsfond (R34)', 'Högst 30 % av överskottet vid R33.'],
        ['okningExpansionsfond', 'Ökning av expansionsfond (R36)', 'Skjuter upp skatt mot expansionsfondsskatt. Högst överskottet vid R35 — och fondens total högst 125,94 % av kapitalunderlaget (IL 34:6).'],
        ['minskningExpansionsfond', 'Minskning av expansionsfond (R37)', 'Återför tidigare avsatt expansionsfond.'],
        ['ingaendeExpansionsfond', 'Ingående expansionsfond (kr)', 'Fondens storlek vid årets ingång — krävs vid ökning (R36) för takregeln.'],
        ['expansionsfondUnderlag', 'Kapitalunderlag för expansionsfond (kr)', 'Vid beskattningsårets UTGÅNG (IL 34:7) — en annan storhet än räntefördelningens underlag.'],
        ['underskottForegaende', 'Outnyttjat underskott från föregående år (R24)', 'Beloppet står på förra årets NE, ruta R48.'],
        ['medgivnaEgenavgifterFg', 'Föregående års schablonavdrag (R40)', 'R43 i förra årets NE-bilaga.'],
        ['paforadeEgenavgifterFg', 'Påförda egenavgifter enligt slutskattebesked (R41)', 'Enligt slutskattebeskedet.'],
    ]],
    ['Bokförda/ej bokförda poster som ska justeras (R13–R16)',
        'Nästan varje firma har någon: kostnader bokförda men inte avdragsgilla, eller intäkter som inte ska beskattas.', [
            ['ejAvdragsgillaKostnader', 'Bokförda kostnader som inte ska dras av (R13)', 'T.ex. representation över schablonen, böter och viten, medlemsavgifter, privata andelar.'],
            ['ejSkattepliktigaIntakter', 'Bokförda intäkter som inte ska tas upp (R14)', 'T.ex. skattefria försäkringsersättningar, vissa bidrag, ränta på skattekontot.'],
            ['ejBokfordaIntakter', 'Intäkter som inte bokförts men ska tas upp (R15)', 'T.ex. privat användning av tillgångar (uttagsbeskattning) som inte bokförts.'],
            ['ejBokfordaKostnader', 'Kostnader som inte bokförts men ska dras av (R16)', 'T.ex. avdragsgill del av utgifter som betalats privat.'],
        ]],
    ['Gemensam verksamhet & medhjälpande make (R18–R20)',
        'Bara när verksamheten drivs gemensamt eller make hjälper till i verksamheten.', [
            ['gemensamOverskottAnnan', 'Överskott som redovisas av annan (R18)', 'Din medbrukares andel av överskottet.'],
            ['gemensamUnderskottAnnan', 'Underskott som redovisas av annan (R19)', 'Din medbrukares andel av underskottet.'],
            ['medhjalpandeMakeAndel', 'Överfört från make — min andel (R20 +)', 'Andel av makens verksamhet som du redovisar.'],
            ['andelTillMedhjalpandeMake', 'Överfört till medhjälpande make (R20 −)', 'Marknadsmässig ersättning för makens arbetsinsats.'],
        ]],
    ['Övriga skattemässiga justeringar (R22–R23)', null, [
        ['ovrigaJusteringarKostnad', 'Övriga justeringar, kostnader (R22)', 'Poster som minskar resultatet skattemässigt och inte hör hemma på någon annan rad.'],
        ['ovrigaJusteringarIntakt', 'Övriga justeringar, intäkter (R23)', 'Poster som ökar resultatet skattemässigt och inte hör hemma på någon annan rad.'],
    ]],
    ['Skogs- och vattenbruk (R25–R28)',
        'Bara för skogsägare och verksamheter med skogs-/upphovsmannakonto.', [
            ['skogsavdrag', 'Skogsavdrag/substansminskningsavdrag (R25)', 'Enligt bilaga N8.'],
            ['aterforingVardeminskning', 'Återföring av värdeminskningsavdrag vid avyttring (R26)', 'När näringsfastighet avyttrats.'],
            ['uttagSkogskonto', 'Uttag från skogs-/upphovsmannakonto (R27)', 'Uttaget beskattas det år det görs.'],
            ['inbetalningSkogskonto', 'Inbetalning till skogs-/upphovsmannakonto (R28)', 'Insättningen dras av insättningsåret.'],
        ]],
    ['Pension, sjukpenning & egenavgifter (R38–R39, R44, avgiftsfritt underlag)', null, [
        ['egnaPensionspremier', 'Egna pensionspremier i näringsverksamheten (R38)', 'Pensionssparavdrag hänförligt till verksamheten.'],
        ['slpPaPensionssparavdrag', 'Särskild löneskatt på pensionssparavdraget (R39)', '24,26 % på beloppet i R38.'],
        ['sjukpenning', 'Sjukpenning hänförlig till verksamheten (R44)', 'Tas upp EFTER schablonavdraget — ingår inte i avgiftsunderlaget.'],
        ['avgiftsfrittUnderlag', 'Inkomster där uppdragsgivare betalat arbetsgivaravgifter (kr)', 'Minskar underlaget för schablonavdraget R43 (IL 16 kap. 30 § p. 2).'],
    ]],
    ['Underskott sedan tidigare (R45–R46)', null, [
        ['allmantAvdragUnderskott', 'Allmänt avdrag — utnyttjat underskott (R45)', 'Kräver laglig grund: nystartad verksamhet (max 100 000 kr/år) eller konstnärlig/litterär — frågorna visas när ett belopp anges.'],
        ['underskottIKapital', 'Underskott utnyttjat i kapital (R46)', 'T.ex. mot kapitalvinst på näringsfastighet.'],
    ]],
];

/**
 * Deklarationssteget för ENSKILD FIRMA — NE-bilagan till INK1.
 * Sida 1 (B/R) kommer från bokföringen, sida 2 (R12–R48) räknas fram av
 * NeComputationCore utifrån dina deklarationsbeslut. Vi skapar SRU-filerna;
 * du lämnar in dem själv.
 */
const NeDeklarationStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [preview, setPreview] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState(false);
    const [generationEvidence, setGenerationEvidence] = useState(null);
    const [filingConfirmed, setFilingConfirmed] = useState(false);
    const [generateError, setGenerateError] = useState(null);
    const [overrideDrafts, setOverrideDrafts] = useState({});
    const [savingOverride, setSavingOverride] = useState(null);
    const [openingK1, setOpeningK1] = useState(false);
    const [openingArsbokslut, setOpeningArsbokslut] = useState(false);
    const [showAllFields, setShowAllFields] = useState(false);

    const fetchPreview = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.getNePreview(companyId, processId);
            setPreview(resp?.data || null);
            const decisions = { ...(resp?.data?.decisions || {}) };
            // R31 FÖRIFYLLS när negativ räntefördelning är obligatorisk
            // (IL 33:4) — backend vägrar numera generera utan den, och
            // beloppet är redan uträknat i gates.
            const g = resp?.data?.rantefordelning;
            if (g?.negativObligatorisk && !decisions.negativRantefordelning
                && g.negativtFordelningsbelopp != null) {
                decisions.negativRantefordelning = String(g.negativtFordelningsbelopp);
            }
            // Verksamhetens art (7020) förifylls från SNI-beskrivningen
            // (Bolagsverket via TIC) när fältet är tomt — alltid redigerbart;
            // utan berikning förblir fältet tomt som idag.
            if (!decisions.verksamhetensArt && resp?.data?.suggestions?.verksamhetensArt) {
                decisions.verksamhetensArt = resp.data.suggestions.verksamhetensArt;
            }
            setDrafts(decisions);
        } catch (err) {
            setLoadError(err.response?.data?.message
                || 'Kunde inte ta fram NE-underlaget. Kontrollera att årets resultat är bokfört (steg 4).');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId]);

    useEffect(() => { fetchPreview({ silent: true }); }, [fetchPreview]);

    const coverage = preview?.coverage;
    const chain = preview?.chain;
    const gates = preview?.rantefordelning;
    const bitratt = drafts.bitratt;
    const canGenerate = Boolean(coverage?.complete && chain && bitratt != null && !generating);

    const setField = (key) => (e) => setDrafts((d) => ({ ...d, [key]: e.target.value }));

    const numeric = (obj) => {
        const out = {};
        for (const [k, v] of Object.entries(obj)) {
            // TOMT FÄLT SKICKAS SOM null — backend-mergen behåller annars det
            // tidigare sparade värdet och ett rensat fält "släpper aldrig"
            // (upptäckt i browserverifieringen 2026-08-10: R13 rensad i UI:t
            // men R17 stod kvar på det gamla talet).
            if (v === '' || v == null) { out[k] = null; continue; }
            out[k] = ['bitratt', 'passivNaringsverksamhet', 'avgiftskohort', 'verksamhetensArt',
                'allmantAvdragGrund', 'allmantAvdragVillkorBekraftade'].includes(k)
                ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'));
        }
        return out;
    };

    const handleSave = async (overrides) => {
        setSaving(true);
        try {
            // overrides: spara ett just bekräftat värde direkt (förslags-
            // knapparna) utan att vänta på nästa render av drafts.
            const payload = overrides && !overrides.target ? { ...drafts, ...overrides } : drafts;
            const resp = await YearEndService.saveNeDecisions(companyId, processId, numeric(payload));
            setPreview(resp?.data || null);
            toast.success('Uppgifterna är sparade.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara uppgifterna.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveOverride = async (accountNumber) => {
        const code = (overrideDrafts[accountNumber] || '').trim();
        if (!code) return;
        setSavingOverride(accountNumber);
        try {
            await YearEndService.setSruOverride(companyId, accountNumber, code);
            toast.success(`Konto ${accountNumber} kopplades till fältkod ${code}.`);
            setOverrideDrafts((d) => ({ ...d, [accountNumber]: '' }));
            await fetchPreview({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara fältkoden.');
        } finally {
            setSavingOverride(null);
        }
    };

    const handleOpenK1 = async () => {
        setOpeningK1(true);
        try {
            await YearEndService.openK1ArsbokslutPdf(companyId, processId);
        } catch (err) {
            toast.error(err.response?.data?.message
                || err.message
                || 'Kunde inte skapa det förenklade årsbokslutet.');
        } finally {
            setOpeningK1(false);
        }
    };

    const handleOpenEfArsbokslut = async () => {
        setOpeningArsbokslut(true);
        try {
            await YearEndService.openEfArsbokslutPdf(companyId, processId);
        } catch (err) {
            toast.error(err.response?.data?.message
                || err.message
                || 'Kunde inte skapa årsbokslutet.');
        } finally {
            setOpeningArsbokslut(false);
        }
    };

    const downloadBase64 = (filename, contentBase64) => {
        const bytes = Uint8Array.from(atob(contentBase64), (ch) => ch.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setGenerateError(null);
        try {
            const resp = await YearEndService.generateNeFiles(companyId, processId, numeric(drafts));
            (resp?.data?.files || []).forEach((f) => downloadBase64(f.filename, f.contentBase64));
            setGenerated(true);
            setGenerationEvidence(resp?.data?.evidence || null);
            setFilingConfirmed(false);
            toast.success('SRU-filerna är skapade och nedladdade.');
        } catch (err) {
            setGenerateError(err.response?.data?.message || 'Kunde inte skapa SRU-filerna.');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <>
            <SkvStyles />
            <style>{`
                .ne-info-btn { background: none; border: none; padding: 0 2px; color: var(--bs-secondary-color, #6c757d); vertical-align: middle; cursor: help; }
                .ne-info-btn:hover, .ne-info-btn:focus-visible { color: var(--primary); outline: none; }
            `}</style>

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        Deklarera (NE-bilaga)
                        {preview?.fiscalYear?.year ? ` — räkenskapsåret ${preview.fiscalYear.year}` : ''}
                    </h4>
                    <p className="section-subtitle">
                        Sidan 1 (B- och R-poster) hämtas direkt ur din bokföring. Sidan 2 är de
                        skattemässiga justeringarna — där bestämmer du räntefördelning,
                        periodiseringsfond och expansionsfond. NE-bilagan lämnas tillsammans med din
                        inkomstdeklaration (INK1) på ditt personnummer.
                    </p>
                    {/* SFL 32:1 — fysisk person; helgdag flyttar fram fristen. */}
                    {preview?.deklarationsdeadline?.date && (
                        <p className="section-subtitle" style={{ marginTop: 4 }}>
                            <strong>Deklarationsfrist:</strong> senast{' '}
                            {preview.deklarationsdeadline.date} (SFL 32 kap. 1 § — 2 maj året efter
                            beskattningsåret). Infaller dagen på helg flyttas fristen fram — se
                            Skatteverkets publicerade datum.
                        </p>
                    )}
                </div>
                <div className="action-buttons">
                    <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchPreview()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'ye-spin' : undefined} />
                        Uppdatera
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
            {loadError && (
                <div className="alert alert-warning d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    <span>{loadError}</span>
                </div>
            )}

            {preview && !coverage?.complete && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <AlertTriangle size={16} style={{ color: 'var(--bs-warning)', marginRight: 6 }} />
                        {coverage.unmapped.length} konton behöver en deklarationsrad
                    </h5>
                    <p className="text-muted" style={{ maxWidth: '68ch' }}>
                        Dessa konton har saldon som inte kan placeras automatiskt på NE-bilagan.
                        Välj vilken B- eller R-post de hör till.
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr><th>Konto</th><th>Namn</th><th className="text-end">Belopp</th><th style={{ width: 220 }}>Fältkod</th></tr>
                            </thead>
                            <tbody>
                                {coverage.unmapped.map((row) => (
                                    <tr key={row.accountNumber}>
                                        <td>{row.accountNumber}</td>
                                        <td>{row.accountName}</td>
                                        <td className="text-end">{formatSek(row.amount)}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                {row.reason === 'vat_field_42_requires_r1_r2_decision' ? (
                                                    <NeRevenueChoice
                                                        accountNumber={row.accountNumber}
                                                        matches={row.matches}
                                                        value={overrideDrafts[row.accountNumber] || ''}
                                                        onChange={(value) => setOverrideDrafts((d) => ({ ...d, [row.accountNumber]: value }))}
                                                        disabled={savingOverride === row.accountNumber}
                                                    />
                                                ) : <input
                                                    className="form-control form-control-sm"
                                                    placeholder="t.ex. 7501"
                                                    value={overrideDrafts[row.accountNumber] || ''}
                                                    onChange={(e) => setOverrideDrafts((d) => ({ ...d, [row.accountNumber]: e.target.value }))}
                                                />}
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    disabled={savingOverride === row.accountNumber || !(overrideDrafts[row.accountNumber] || '').trim()}
                                                    onClick={() => handleSaveOverride(row.accountNumber)}
                                                >
                                                    {savingOverride === row.accountNumber ? 'Sparar…' : 'Spara'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {gates && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">Räntefördelning</h5>
                    <div className="ye-well">
                        {gates.positivTillaten ? (
                            <>
                                Med ett kapitalunderlag på {formatSek(gates.kapitalunderlag)} får du i år
                                flytta upp till <strong>{formatSek(gates.positivtFordelningsbelopp)}</strong> av
                                överskottet till kapitalbeskattning ({fmtPct(gates.positivRate)} %). Det är frivilligt.
                            </>
                        ) : (
                            <>Positiv räntefördelning är inte aktuell med nuvarande kapitalunderlag.</>
                        )}
                        {gates.negativObligatorisk && (
                            <div className="mt-2">
                                <strong>Negativ räntefördelning är obligatorisk</strong> — kapitalunderlaget är
                                lägre än {formatSek(gates.negativGrans)} kr.
                                Belopp: {formatSek(gates.negativtFordelningsbelopp)} ({gates.negativRate} %).
                            </div>
                        )}
                        <div className="ye-field-help mt-2">
                            Regelverk: {gates.regelset === 'fran_2025'
                                ? 'räkenskapsår som börjar 2025 eller senare (inget lägsta kapitalunderlag för positiv räntefördelning)'
                                : 'räkenskapsår som började före 2025 (gamla ±50 000-gränserna gäller)'}.
                        </div>
                    </div>
                    <div className="ye-field" style={{ maxWidth: 420, marginTop: '0.75rem' }}>
                        <label htmlFor="ne-kapitalunderlag">
                            Kapitalunderlag för räntefördelning (kr)
                            {' '}
                            <InfoHover id="ne-help-kapu" label="Kapitalunderlaget (IL 33 kap. 8 §)">
                                Skillnaden mellan tillgångar och skulder i verksamheten vid det
                                föregående beskattningsårets utgång (= årets ingående balans),
                                med justeringar: föregående års ej avdragna underskott läggs
                                till (33 kap. 10 §), tillfälliga kapitaltillskott dras ifrån,
                                övergångsposten från 1995 läggs till (33 kap. 14 §), sparat
                                fördelningsbelopp läggs till, och periodiseringsfonder räknas
                                som skuld medan expansionsfonden avgår med 79,4 %. Är underlaget
                                under −500 000 kr är negativ räntefördelning obligatorisk
                                (33 kap. 4 §); ett positivt underlag ger rätt till positiv
                                räntefördelning (33 kap. 3 §). Ett tomt fält är inte noll —
                                utan beloppet skapas ingen NE-bilaga. Se hjälpblankett SKV 2196.
                            </InfoHover>
                        </label>
                        <input
                            id="ne-kapitalunderlag"
                            type="text"
                            inputMode="decimal"
                            value={drafts.kapitalunderlag ?? ''}
                            onChange={setField('kapitalunderlag')}
                            onBlur={handleSave}
                        />
                        {(drafts.kapitalunderlag == null || drafts.kapitalunderlag === '')
                            && preview?.suggestions?.kapitalunderlag && (
                            <div className="ye-field-help" style={{ marginTop: '0.4rem' }}>
                                Härlett ur ingående balansen: <strong>{formatSek(preview.suggestions.kapitalunderlag.bas)}</strong>
                                {' '}(tillgångar {formatSek(preview.suggestions.kapitalunderlag.tillgangar)}
                                {' '}− skulder {formatSek(preview.suggestions.kapitalunderlag.skulder)}
                                {preview.suggestions.kapitalunderlag.expansionsfondAvdrag
                                    ? <> − expansionsfond {formatSek(preview.suggestions.kapitalunderlag.expansionsfondAvdrag)}</>
                                    : null}).
                                {' '}Justeringar systemet inte ser (underskott, kapitaltillskott,
                                övergångspost, sparat belopp) läggs på för hand.{' '}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ marginLeft: '0.4rem', padding: '0 0.5rem' }}
                                    onClick={() => {
                                        const v = String(preview.suggestions.kapitalunderlag.bas);
                                        setDrafts((d) => ({ ...d, kapitalunderlag: v }));
                                        handleSave({ kapitalunderlag: v });
                                    }}
                                >
                                    Bekräfta förslaget
                                </button>
                            </div>
                        )}
                        <div className="ye-field-help">
                            Måste anges — styr både rätten till positiv och plikten till negativ
                            räntefördelning.
                        </div>
                    </div>
                </div>
            )}

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">Dina deklarationsbeslut</h5>
                <div className="ye-field" style={{ maxWidth: 480 }}>
                    <label htmlFor="ne-kohort">
                        Egenavgifter (avgiftskohort)
                        {' '}
                        <InfoHover id="ne-help-kohort" label="Avgiftskohorten styr R43">
                            Schablonavdraget för egenavgifter/SLF på rad R43 beror på vem som
                            betalar vilka avgifter (SAL 3 kap. 15–16 §§; avdragets tak i
                            IL 16 kap. 30 §): född 1959 eller senare betalar fulla egenavgifter
                            (avdrag högst 25 %); född 1938–1958 — eller den som tagit ut hel
                            ålderspension — betalar bara ålderspensionsavgift 10,21 % (avdrag
                            10 %); född 1937 eller tidigare betalar inga avgifter (0 %).
                            Passiv verksamhet och dödsbon betalar särskild löneskatt (avdrag
                            högst 20 %). Källa: Skatteverkets tabell &quot;Avgift och
                            schablonavdrag&quot;. Fel kohort ger fel skatt — därför måste
                            valet bekräftas.
                        </InfoHover>
                    </label>
                    <select
                        id="ne-kohort"
                        className="form-select"
                        value={drafts.avgiftskohort || ''}
                        onChange={setField('avgiftskohort')}
                        onBlur={handleSave}
                    >
                        <option value="" disabled>— välj kohort —</option>
                        {KOHORTER.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                    </select>
                    {!drafts.avgiftskohort && preview?.suggestions?.avgiftskohort && (
                        <div className="ye-field-help" style={{ marginTop: '0.4rem' }}>
                            Förslag utifrån födelseåret {preview.suggestions.avgiftskohort.birthYear}:{' '}
                            <strong>{(KOHORTER.find(([v]) => v === preview.suggestions.avgiftskohort.value) || ['', preview.suggestions.avgiftskohort.value])[1]}</strong>
                            {' '}— {preview.suggestions.avgiftskohort.basis}{' '}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                style={{ marginLeft: '0.4rem', padding: '0 0.5rem' }}
                                onClick={() => {
                                    setDrafts((d) => ({ ...d, avgiftskohort: preview.suggestions.avgiftskohort.value }));
                                    handleSave({ avgiftskohort: preview.suggestions.avgiftskohort.value });
                                }}
                            >
                                Bekräfta förslaget
                            </button>
                        </div>
                    )}
                    <div className="ye-field-help">
                        Styr schablonavdraget på rad R43. Måste bekräftas — utan kohort skapas
                        ingen NE-bilaga.
                    </div>
                </div>
                {DECISION_GROUPS.map(([groupName, groupHelp, fields], gi) => {
                    if (gi > 0 && !showAllFields && !fields.some(([key]) => drafts[key])) return null;
                    return (
                        <div key={groupName} style={{ marginTop: '0.75rem' }}>
                            {gi > 0 && (
                                <>
                                    <h6 style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>{groupName}</h6>
                                    {groupHelp && <div className="ye-field-help" style={{ marginBottom: '0.5rem' }}>{groupHelp}</div>}
                                </>
                            )}
                            <div className="ye-form-grid">
                                {fields.map(([key, label, help]) => (
                                    <div className="ye-field" key={key}>
                                        <label htmlFor={`ne-${key}`}>{label}</label>
                                        <input
                                            id={`ne-${key}`}
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0"
                                            value={drafts[key] ?? ''}
                                            onChange={setField(key)}
                                            onBlur={handleSave}
                                        />
                                        <div className="ye-field-help">{help}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
                {Number(drafts.allmantAvdragUnderskott) > 0 && (
                    <div className="alert alert-warning" style={{ fontSize: '0.875rem', maxWidth: 640, marginTop: '0.75rem' }}>
                        <strong>R45 kräver en laglig grund</strong> (IL 62 kap. 2–4 §§) —
                        utan den vägrar filgenereringen.
                        <div className="ye-field" style={{ marginTop: '0.5rem' }}>
                            <label htmlFor="ne-allmantAvdragGrund">Grund för avdraget</label>
                            <select
                                id="ne-allmantAvdragGrund"
                                value={drafts.allmantAvdragGrund || ''}
                                onChange={(e) => { setField('allmantAvdragGrund')(e); }}
                                onBlur={handleSave}
                            >
                                <option value="">Välj grund…</option>
                                <option value="nystartad">
                                    Nystartad aktiv verksamhet (max 100 000 kr/år, startåret + 4 år)
                                </option>
                                <option value="konstnarlig">
                                    Konstnärlig/litterär verksamhet (inget beloppstak)
                                </option>
                            </select>
                        </div>
                        <label className="mb-0 d-flex align-items-start gap-2" style={{ marginTop: '0.5rem' }}>
                            <input
                                type="checkbox"
                                checked={drafts.allmantAvdragVillkorBekraftade === true}
                                onChange={(e) => {
                                    setDrafts((d) => ({ ...d, allmantAvdragVillkorBekraftade: e.target.checked }));
                                }}
                                onBlur={handleSave}
                                style={{ marginTop: 3 }}
                            />
                            <span>
                                {drafts.allmantAvdragGrund === 'konstnarlig'
                                    ? 'Jag bekräftar att verksamheten är uteslutande eller så gott som uteslutande konstnärlig/litterär, att avdraget bara avser årets underskott, och att intäkterna de senaste fyra åren (inklusive tjänst) inte är obetydliga.'
                                    : 'Jag bekräftar att verksamheten är aktiv, att detta är startåret eller något av de fyra följande åren, att jag inte bedrivit liknande verksamhet (direkt eller indirekt) under de fem åren före starten, och att verksamheten inte förvärvats från närstående med begränsad avdragsrätt.'}
                            </span>
                        </label>
                    </div>
                )}
                <button
                    type="button"
                    className="btn btn-link btn-sm px-0"
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => setShowAllFields((v) => !v)}
                >
                    {showAllFields
                        ? 'Visa bara de vanligaste posterna'
                        : 'Visa fler poster (ej avdragsgilla kostnader, skogsbruk, gemensam verksamhet, pension m.m.)'}
                </button>
                {saving && <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Sparar…</span>}
                {preview?.chainError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem', maxWidth: 640 }}>
                        {preview.chainError.message}
                    </div>
                )}
            </div>

            {chain && (
                <div className="ye-ps-section">
                    <div className="skv-sheet">
                        <div className="skv-sheet-head">
                            <div>
                                <span className="skv-form-chip">NE</span>
                                <span className="skv-form-title">Skattemässiga justeringar</span>
                            </div>
                            <div className="skv-period">
                                NE-bilagan sida 2 — rad R12–R48
                            </div>
                        </div>
                        {R_ROWS.map(([row, label, editable]) => {
                            const value = chain.rows[row];
                            if (!value && editable) return null;
                            return (
                                <div className={`skv-row${editable ? '' : ' skv-row-strong'}`} key={row}>
                                    <span className="skv-punkt">{row}</span>
                                    <span className="skv-label">{label}</span>
                                    <span className="skv-box">
                                        {/* Hela kronor utan enhet — samma sifferformat som i
                                            SRU-filen och INK2-blanketten. */}
                                        <span className="skv-belopp">{Number(value ?? 0).toLocaleString('sv-SE')}</span>
                                    </span>
                                </div>
                            );
                        })}
                        <p className="skv-note">
                            Schablonavdraget för egenavgifter är {chain.schablon.procent} % av{' '}
                            {formatSek(chain.schablon.underlag)}. Överskottet vid R47 förs till din
                            inkomstdeklaration (INK1). Rader utan belopp lämnas tomma i filen och
                            visas inte här.
                        </p>
                    </div>
                </div>
            )}

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">Uppgifter till Skatteverket</h5>
                <div className="ye-field" style={{ maxWidth: 480 }}>
                    <label htmlFor="ne-verksamhet">Verksamhetens art</label>
                    <input
                        id="ne-verksamhet"
                        type="text"
                        maxLength={80}
                        value={drafts.verksamhetensArt ?? ''}
                        onChange={setField('verksamhetensArt')}
                        onBlur={handleSave}
                        placeholder="t.ex. Frisörverksamhet"
                    />
                </div>
                <div className="skv-question" style={{ marginTop: '0.75rem', maxWidth: 860 }}>
                    <span className="skv-label">
                        Har en uppdragstagare (t.ex. redovisningskonsult) biträtt vid
                        upprättandet av årsbokslutet?
                    </span>
                    <SkvSeg
                        name="Uppdragstagare har biträtt"
                        value={bitratt}
                        onChange={(v) => { setDrafts((d) => ({ ...d, bitratt: v })); }}
                    />
                </div>
                {generateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>{generateError}</div>
                )}
                <div className="ye-proposal-actions">
                    <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleGenerate}
                        disabled={!canGenerate}
                    >
                        <Download size={16} />
                        {generating ? 'Skapar filer…' : 'Skapa och ladda ner SRU-filer'}
                    </button>
                </div>
                {bitratt == null && (
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8125rem' }}>
                        Besvara frågan ovan för att kunna skapa filerna.
                    </p>
                )}
            </div>

            {preview?.processType === 'ef_forenklat' && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <FileText size={16} style={{ marginRight: 6 }} />
                        Förenklat årsbokslut (K1)
                    </h5>
                    <p className="text-muted" style={{ maxWidth: '68ch' }}>
                        Uppställningsformen i BFNAR 2006:1 bilaga 2 kräver upplysning om
                        fondernas <strong>summa vid årets slut</strong> (rutorna U1–U4).
                        Avsättningarna görs i deklarationen, inte i bokföringen, så de går
                        inte att hämta ur saldona.
                    </p>
                    <div className="ye-form-grid" style={{ marginBottom: '1rem' }}>
                        {U_FIELDS.map(([key, label]) => (
                            <div className="ye-field" key={key}>
                                <label htmlFor={`ne-${key}`}>{label}</label>
                                <input
                                    id={`ne-${key}`}
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={drafts[key] ?? ''}
                                    onChange={setField(key)}
                                    onBlur={handleSave}
                                />
                            </div>
                        ))}
                    </div>
                    <p className="text-muted" style={{ maxWidth: '68ch' }}>
                        Årsbokslutet är samma siffror som NE-bilagans sida 1. Det ska
                        skrivas under och arkiveras tillsammans med räkenskaps­informationen —
                        men det lämnas <strong>aldrig in</strong> till Skatteverket eller
                        Bolagsverket. Det är NE-bilagan som lämnas in.
                    </p>
                    <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={handleOpenK1}
                        disabled={openingK1 || !coverage?.complete}
                    >
                        <FileText size={14} />
                        {openingK1 ? 'Öppnar…' : 'Öppna förenklat årsbokslut'}
                    </button>
                    {!coverage?.complete && (
                        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8125rem' }}>
                            Komplettera fältkoderna ovan först — annars saknas poster i årsbokslutet.
                        </p>
                    )}
                </div>
            )}

            {preview?.processType === 'ef_arsbokslut' && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <FileText size={16} style={{ marginRight: 6 }} />
                        Årsbokslut (BFNAR 2017:3)
                    </h5>
                    <p className="text-muted" style={{ maxWidth: '68ch' }}>
                        Din firma upprättar ett vanligt årsbokslut enligt Bokförings­nämndens
                        K-regelverk Årsbokslut (BFNAR 2017:3) — det är så den löpande
                        bokföringen formellt avslutas (6 kap. bokföringslagen). Dokumentet
                        ska skrivas under och arkiveras tillsammans med
                        räkenskaps­informationen till och med det sjunde året efter
                        räkenskapsårets slut — men det lämnas <strong>aldrig in</strong> till
                        Skatteverket eller Bolagsverket. Det är NE-bilagan som lämnas in.
                    </p>
                    <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={handleOpenEfArsbokslut}
                        disabled={openingArsbokslut}
                    >
                        <FileText size={14} />
                        {openingArsbokslut ? 'Öppnar…' : 'Öppna årsbokslut'}
                    </button>
                </div>
            )}

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">
                    <ShieldCheck size={16} style={{ marginRight: 6 }} />
                    Så lämnar du in
                </h5>
                <ol className="mb-2" style={{ maxWidth: '68ch' }}>
                    <li>Ladda ner <code>info.sru</code> och <code>blanketter.sru</code> ovan.</li>
                    <li>Ladda upp båda filerna i Skatteverkets filöverföringstjänst — NE-bilagan kopplas
                        till din inkomstdeklaration (INK1).</li>
                    <li>Kontrollera kvittensen — först då är bilagan inlämnad.</li>
                </ol>
                <a
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                    href={FILOVERFORING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Öppna Skatteverkets filöverföring
                    <ExternalLink size={14} />
                </a>
            </div>

            {gateError && (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    <span>{gateError.message}</span>
                </div>
            )}

            {!completed && (
                <FilingSubmissionPanel
                    companyId={companyId}
                    processId={processId}
                    generatedEvidence={generationEvidence}
                    onStatusChange={setFilingConfirmed}
                />
            )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            NE-bilagan är markerad som inlämnad
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {generated
                                ? 'När du fått Skatteverkets kvittens — markera steget som klart.'
                                : 'Skapa filerna och lämna in dem hos Skatteverket först.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy || !filingConfirmed}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default NeDeklarationStep;
